import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { GROUP_KEYS } from "../helpers/evalComparisonApi";

const SORT_FIELDS = ["script", "line_order", "question", "accepted"];

// Production is the engine version live today; test is the one being tried.
// Use the run's recorded roles, else CWS (or the first version) as production
// and the next version as test.
export const defaultRoles = (presets, roles = {}) => {
  const production = presets.includes(roles.production)
    ? roles.production
    : presets.includes("cws")
      ? "cws"
      : (presets[0] ?? null);
  const others = presets.filter((p) => p !== production);
  const test = others.includes(roles.test) ? roles.test : (others[0] ?? null);
  return { production, test };
};

// Reads the run page's filters from the URL (prod, test, by, group, script,
// diff, sort, order) so a reload or a shared link shows the same view. Unknown
// values fall back to the defaults without an error and without rewriting the
// URL. In the view, `a` is production and `b` is test (the diff's preset_a and
// preset_b); `by` says which one the outcome groups use.
const useComparisonViewState = ({ presets, scripts, roles }) => {
  const [params, setParams] = useSearchParams();
  const defaults = useMemo(() => defaultRoles(presets, roles), [presets, roles]);

  const view = useMemo(() => {
    const pick = (key, allowed, fallback) => (allowed.includes(params.get(key)) ? params.get(key) : fallback);
    const a = pick("prod", presets, defaults.production);
    const testChoices = presets.filter((p) => p !== a);
    const b = testChoices.length
      ? pick("test", testChoices, testChoices.includes(defaults.test) ? defaults.test : testChoices[0])
      : null;
    return {
      a,
      b,
      by: b && params.get("by") !== "prod" ? "b" : "a",
      group: pick("group", GROUP_KEYS, "all"),
      script: pick("script", scripts, ""),
      diff: Boolean(b) && params.get("diff") === "1",
      sort: pick("sort", SORT_FIELDS, null),
      order: params.get("order") === "desc" ? "desc" : "asc",
    };
  }, [params, presets, scripts, defaults]);

  // Writes only what differs from the defaults, and keeps other query fields (run_id).
  const update = useCallback(
    (patch) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          const merged = { ...view, ...patch };
          const write = (key, value, isDefault) => (isDefault ? next.delete(key) : next.set(key, value));
          write("prod", merged.a, !merged.a || merged.a === defaults.production);
          write("test", merged.b, !merged.b || merged.b === defaults.test);
          write("by", "prod", !merged.b || merged.by !== "a");
          write("group", merged.group, merged.group === "all");
          write("script", merged.script, !merged.script);
          write("diff", "1", !merged.diff);
          write("sort", merged.sort, !merged.sort);
          write("order", merged.order, !merged.sort || merged.order !== "desc");
          return next;
        },
        { replace: true },
      );
    },
    [setParams, view, defaults],
  );

  return { view, update };
};

export default useComparisonViewState;
